// Money/date formatting helpers for the Money screen (Slice 6; D58; ADR-0023).
//
// D58 (docs/phase-0/decisions-adr.md:127): every customer-facing price is displayed in dollars; EUR
// stays the internal accounting currency. Every money figure this screen renders carries its own
// `currency` field (always USD in practice per D58) and needs no conversion, only formatting.
//
// THIS HEADER USED TO NAME `apps.monthlyCeilingMinor` AS THE ONE EXCEPTION - "stored in EUR and must
// be CONVERTED for display" - and that is FALSE. ADR-0039 settled the column as minor units of the
// account's PLAN currency, USD for every shipped plan, and PR #46 recorded the display path as a
// defect for AC3 to fix. AC3 landed, `MoneyTab` stopped converting, and this header survived
// unchanged until a verification panel found it. Corrected here rather than left as a loaded gun:
// the two FX helpers below now have NO callers, and a contributor reading the old paragraph would
// have been told to reintroduce the very conversion that overstated a founder's own guardrail by
// 1.1696x - a $500 limit rendered as $584.80.
//
// THE HELPERS ARE KEPT, not deleted, and the distinction matters. They encode D58's modeled rate,
// which is real and cited; what was wrong was the claim about WHICH FIGURE needs them. A genuine
// EUR-denominated customer figure would still want them. Nothing on this screen is one today.
//
// Referenced by apps/web/src/api/money.ts's own header comment ("see
// ../screens/money/format.js's usdMinorToEurMinor") - this module is that promised home.

/** The modeled FX rate (D58, stack-profile.md:82-83): $1.00 = EUR 0.855. A currency
 * *display* conversion, never a "modeled estimate" figure of its own - the underlying
 * ceiling value is exact, only its display currency changes (ux spec §1 rule 4). */
export const EUR_PER_USD_MODELED_FX = 0.855;

/** Guards non-finite input (app-engineering §2: a threshold/conversion gate fails closed
 * on NaN/Infinity, never silently produces one). Returns `null` on a non-finite input
 * rather than propagating a broken figure into a customer-facing render. */
const finiteOrNull = (value: number): number | null => (Number.isFinite(value) ? value : null);

/** EUR minor units (cents) -> USD minor units (cents), at the modeled FX rate. */
export const eurMinorToUsdMinor = (eurMinor: number): number | null => {
  const usd = finiteOrNull(eurMinor / EUR_PER_USD_MODELED_FX);
  return usd === null ? null : Math.round(usd);
};

/** USD minor units (cents) -> EUR minor units (cents), at the modeled FX rate - the wire
 * direction `apps/web/src/api/money.ts`'s PROPOSED `changeCeiling` call needs (the founder
 * types a USD ceiling; `apps.monthlyCeilingMinor` stores EUR). */
export const usdMinorToEurMinor = (usdMinor: number): number | null => {
  const eur = finiteOrNull(usdMinor * EUR_PER_USD_MODELED_FX);
  return eur === null ? null : Math.round(eur);
};

/** Formats an integer minor-unit amount in `currency` using `Intl.NumberFormat`. Falls
 * back to a plain `"{CODE} {amount}"` string on a non-finite amount or an unrecognized
 * currency code (fail closed to something readable, never a thrown render crash - the
 * `CurrencyCode` schema only checks the three-uppercase-letter SHAPE, money.ts:18, not
 * ISO-4217 registry membership). */
export const formatMinor = (minor: number, currency: string): string => {
  if (!Number.isFinite(minor)) return `${currency} —`;
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(minor / 100);
  } catch {
    return `${currency} ${(minor / 100).toFixed(2)}`;
  }
};

/** Formats an integer USD minor-unit amount - the one money figure this screen always
 * shows in USD regardless of the wire's own currency field (the ceiling, D58/§1 rule 4). */
export const formatUsdMinor = (minor: number): string => formatMinor(minor, "USD");

const RELATIVE_UNITS: readonly { readonly unit: Intl.RelativeTimeFormatUnit; readonly ms: number }[] = [
  { unit: "year", ms: 365 * 24 * 60 * 60 * 1000 },
  { unit: "month", ms: 30 * 24 * 60 * 60 * 1000 },
  { unit: "day", ms: 24 * 60 * 60 * 1000 },
  { unit: "hour", ms: 60 * 60 * 1000 },
  { unit: "minute", ms: 60 * 1000 },
  { unit: "second", ms: 1000 },
];

/** A short, locale-aware relative-time string ("2 days ago", "just now") for the billing-
 * inbox row's "added {relative}" copy slot (money.ts copy key
 * `money.billingInbox.row.periodAdded`). `now` is injectable for deterministic tests.
 * Fails closed to the raw ISO string on an unparsable `iso` (never a crash, never a
 * fabricated "just now"). */
export const formatRelativeFromNow = (iso: string, now: Date = new Date()): string => {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return iso;
  const diffMs = then - now.getTime();
  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  for (const { unit, ms } of RELATIVE_UNITS) {
    if (Math.abs(diffMs) >= ms || unit === "second") {
      return rtf.format(Math.round(diffMs / ms), unit);
    }
  }
  return rtf.format(0, "second");
};
